import React, { useMemo, useState } from 'react';
import useIsMobile from './useIsMobile';
import { projLabel, sortProjects } from './constants';

// ── UnitCreateModal ──
// Inline unit creation. Used by fresh tenants (e.g. Avia) whose `units` table
// starts empty, or to add a one-off unit that wasn't part of the seeded master
// list. Projects come from `brandProjects` (the same list the lead modal uses
// via /api/config), so a unit registered here matches what sales will pick on
// the lead side. New units default to status='متاح' server-side.
export default function UnitCreateModal({ brandProjects, existingUnits, onClose, onSubmit, t, lang }) {
  const isMobile = useIsMobile();

  const projectOptions = useMemo(
    () => sortProjects([...new Set(brandProjects || [])]),
    [brandProjects]
  );

  const [form, setForm] = useState({
    project: projectOptions[0] || '',
    unit_code: '',
    unit_type: '',
    neighborhood: '',
    base_price: '',
  });
  const u = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  // No pre-defined projects (fresh tenant like Mawreth) → let them type one in.
  const [addingNew] = useState(projectOptions.length === 0);

  const duplicate = useMemo(() => {
    const p = form.project;
    const code = (form.unit_code || '').trim().toUpperCase();
    if (!p || !code) return false;
    return (existingUnits || []).some(
      (x) => x.project === p && (x.unit_code || '').toUpperCase() === code
    );
  }, [form.project, form.unit_code, existingUnits]);

  const validationError = useMemo(() => {
    if (!form.project) return lang === 'ar' ? 'اختر المشروع' : 'Select a project';
    if (!form.unit_code.trim()) return lang === 'ar' ? 'رمز الوحدة مطلوب' : 'Unit code required';
    if (duplicate) return lang === 'ar' ? 'الوحدة موجودة بالفعل في هذا المشروع' : 'Unit already exists in this project';
    const bp = Number(form.base_price || 0);
    if (form.base_price !== '' && !(bp >= 0)) return lang === 'ar' ? 'سعر غير صحيح' : 'Invalid price';
    return null;
  }, [form, duplicate, lang]);

  const submit = async () => {
    if (validationError || saving) return;
    setErr('');
    setSaving(true);
    try {
      await onSubmit({
        project: form.project,
        unit_code: form.unit_code.trim().toUpperCase(),
        unit_type: form.unit_type.trim(),
        neighborhood: form.neighborhood.trim(),
        base_price: Number(form.base_price || 0),
        current_price: Number(form.base_price || 0),
      });
    } catch (e) {
      setSaving(false);
      const msg = e?.response?.data?.error || (lang === 'ar' ? 'فشل الحفظ' : 'Save failed');
      setErr(msg);
    }
  };

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div
        className="crm-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 520,
          maxHeight: isMobile ? '100vh' : '90vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', letterSpacing: 0.4, marginBottom: 2, textTransform: 'uppercase' }}>
              {lang === 'ar' ? 'إضافة وحدة' : 'Add Unit'}
            </div>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 800, margin: 0 }}>
              {lang === 'ar' ? 'وحدة جديدة' : 'New Unit'}
            </h2>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose}>&times;</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '14px 16px' }}>
          <div className="input-group" style={{ marginBottom: 10 }}>
            <label>{lang === 'ar' ? 'المشروع' : 'Project'} <span style={{ color: '#ef4444' }}>*</span></label>
            {addingNew ? (
              <input
                type="text"
                value={form.project}
                onChange={(e) => u('project', e.target.value)}
                placeholder={lang === 'ar' ? 'اسم المشروع' : 'Project name'}
                maxLength={100}
                autoFocus
              />
            ) : (
              <select value={form.project} onChange={(e) => u('project', e.target.value)}>
                <option value="">— {lang === 'ar' ? 'اختر' : 'Select'} —</option>
                {projectOptions.map((p) => (
                  <option key={p} value={p}>{projLabel(p, t)}</option>
                ))}
              </select>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="input-group">
              <label>{lang === 'ar' ? 'رمز الوحدة' : 'Unit Code'} <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                value={form.unit_code}
                onChange={(e) => u('unit_code', e.target.value)}
                placeholder={lang === 'ar' ? 'مثال: A01' : 'e.g. A01'}
                style={{ textTransform: 'uppercase', direction: 'ltr' }}
                maxLength={20}
              />
            </div>
            <div className="input-group">
              <label>{lang === 'ar' ? 'نوع الوحدة' : 'Unit Type'}</label>
              <input
                type="text"
                value={form.unit_type}
                onChange={(e) => u('unit_type', e.target.value)}
                placeholder={lang === 'ar' ? 'فلة، شقة، أرضي…' : 'Villa, Apt…'}
                maxLength={50}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div className="input-group">
              <label>{lang === 'ar' ? 'الحي' : 'Neighborhood'}</label>
              <input
                type="text"
                value={form.neighborhood}
                onChange={(e) => u('neighborhood', e.target.value)}
                placeholder={lang === 'ar' ? 'النرجس…' : 'Area name…'}
                maxLength={100}
              />
            </div>
            <div className="input-group">
              <label>{lang === 'ar' ? 'السعر (SAR)' : 'Price (SAR)'}</label>
              <input
                type="number"
                value={form.base_price}
                onChange={(e) => u('base_price', e.target.value)}
                placeholder="0"
                min="0"
                step="1000"
                style={{ direction: 'ltr' }}
              />
            </div>
          </div>

          {err && (
            <div style={{ background: '#ef44441a', border: '1px solid #ef444440', borderRadius: 8, padding: '6px 10px', fontSize: '0.74rem', color: '#ef4444', fontWeight: 700, marginTop: 6 }}>
              {err}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '10px 14px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-card)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {validationError && (
            <div style={{ background: '#ef44441a', border: '1px solid #ef444440', borderRadius: 8, padding: '6px 10px', fontSize: '0.74rem', color: '#ef4444', fontWeight: 700 }}>
              {validationError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1, padding: '10px' }}>
              {lang === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!!validationError || saving}
              style={{ flex: 2, fontWeight: 800, padding: '10px', opacity: validationError || saving ? 0.6 : 1 }}
            >
              {saving
                ? (lang === 'ar' ? 'جارٍ الحفظ…' : 'Saving…')
                : (lang === 'ar' ? 'إضافة الوحدة' : 'Add Unit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
